import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Clock, MapPin, Globe, Monitor, Activity, Search, X, Ban, Unlock } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function RateLimits() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchIp, setSearchIp] = useState('');
  const [ipHistory, setIpHistory] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState(null);

  const loadStats = async () => {
    try {
      const res = await api.get('/api/admin/rate-limits');
      setStats(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);
  
  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(loadStats, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleIpSearch = async () => {
    if (!searchIp.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/api/admin/rate-limits/history/${searchIp.trim()}`);
      setIpHistory(res.data);
    } catch { toast.error('Failed to fetch IP history'); }
    finally { setSearching(false); }
  };

  const handleBlock = async (ip, permanent = true) => {
    try {
      await api.post('/api/admin/rate-limits/block', { ip, permanent });
      toast.success(`IP ${ip} ${permanent ? 'permanently' : 'temporarily'} blocked`);
      loadStats();
    } catch { toast.error('Failed to block IP'); }
  };

  const handleUnblock = async (ip) => {
    try {
      await api.post('/api/admin/rate-limits/unblock', { ip });
      toast.success(`IP ${ip} unblocked`);
      loadStats();
    } catch { toast.error('Failed to unblock IP'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="loader"></div></div>;
  if (!stats) return <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Failed to load</p>;

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Shield size={22} color="var(--accent-primary)" /> Rate Limiting & Security
          </h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>Real-time forensic analysis of API abuse and attack patterns</p>
        </div>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <KPICard icon={<AlertTriangle size={20} />} label="Active Blocks" value={stats.active_blocks} color="var(--error)" />
        <KPICard icon={<Activity size={20} />} label="Total Violations" value={stats.total_violations} color="var(--warning)" />
        <KPICard icon={<Monitor size={20} />} label="Tracked IPs" value={stats.active_windows} color="var(--accent-primary)" />
        <KPICard icon={<Shield size={20} />} label="Violation Types" value={Object.keys(stats.violation_summary?.by_type || {}).length} color="var(--accent-secondary)" />
      </div>

      {/* Currently Blocked IPs */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)' }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Ban size={16} color="var(--error)" /> Currently Blocked IPs
        </h3>
        {stats.blocked_ips?.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, textAlign: 'center', padding: 20 }}>No IPs currently blocked</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-primary)' }}>
                  <th style={thStyle}>IP Address</th>
                  <th style={thStyle}>Violation</th>
                  <th style={thStyle}>Unblocks In</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stats.blocked_ips.map((b, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <td style={tdStyle}><code style={{ color: 'var(--error)', fontSize: 12 }}>{b.ip}</code></td>
                    <td style={tdStyle}><span style={{ padding: '3px 10px', borderRadius: 10, background: 'var(--error-bg)', color: 'var(--error)', fontSize: 11, fontWeight: 600 }}>{b.violation_description}</span></td>
                    <td style={tdStyle}><Clock size={12} style={{ marginRight: 4 }} />{Math.ceil(b.unblock_in_seconds / 60)} min</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleUnblock(b.ip)} style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}><Unlock size={12} /> Unblock</button>
                        <button onClick={() => handleBlock(b.ip, true)} style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}><Ban size={12} /> Permaban</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Violation Summary Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)' }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>By Violation Type</h4>
          {Object.entries(stats.violation_summary?.by_type || {}).map(([type, count]) => (
            <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-primary)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{type.replace(/_/g, ' ')}</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{count}</span>
            </div>
          ))}
        </div>
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)' }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>By Country</h4>
          {Object.entries(stats.violation_summary?.by_country || {}).sort((a,b) => b[1]-a[1]).slice(0, 10).map(([country, count]) => (
            <div key={country} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-primary)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{country}</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* IP Lookup */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)' }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Search size={16} /> IP Address Lookup
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={searchIp} onChange={e => setSearchIp(e.target.value)} placeholder="Enter IP address..." style={{ flex: 1, padding: '10px 14px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none' }} />
          <button onClick={handleIpSearch} disabled={searching} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: 'var(--accent-gradient)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {searching ? 'Searching...' : 'Lookup'}
          </button>
        </div>
        {ipHistory && (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-tertiary)', borderRadius: 12, border: '1px solid var(--border-primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{ipHistory.ip}</span>
              <span style={{ color: ipHistory.currently_blocked ? 'var(--error)' : 'var(--success)', fontSize: 12, fontWeight: 600 }}>
                {ipHistory.currently_blocked ? '🔴 Blocked' : '🟢 Clear'}
              </span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Total Violations: <strong style={{ color: 'var(--text-primary)' }}>{ipHistory.total_violations}</strong>
            </div>
            {ipHistory.block_info && (
              <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 4 }}>
                Currently blocked: {ipHistory.block_info.type},  unblocks in {Math.round(ipHistory.block_info.unblock_in/60)} min
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Violations,  Detailed Table */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)' }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={16} color="var(--warning)" /> Recent Violations ({stats.recent_violations?.length || 0})
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-primary)' }}>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>IP</th>
                <th style={thStyle}>Location</th>
                <th style={thStyle}>Violation</th>
                <th style={thStyle}>Endpoint</th>
                <th style={thStyle}>Requests</th>
                <th style={thStyle}>Block</th>
                <th style={thStyle}>MAC</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {(stats.recent_violations || []).map((v, i) => (
                <React.Fragment key={v.id}>
                  <tr style={{ borderBottom: '1px solid var(--border-primary)', cursor: 'pointer' }} onClick={() => setSelectedViolation(selectedViolation === v.id ? null : v.id)}>
                    <td style={tdStyle}>{new Date(v.timestamp).toLocaleTimeString()}</td>
                    <td style={tdStyle}><code style={{ color: 'var(--error)', fontSize: 11 }}>{v.ip}</code></td>
                    <td style={tdStyle}>
                      {v.geo ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Globe size={10} /> {v.geo.city}, {v.geo.country}
                        </span>
                      ) : ', '}
                    </td>
                    <td style={tdStyle}><span style={{ padding: '2px 8px', borderRadius: 8, background: 'var(--error-bg)', color: 'var(--error)', fontSize: 10, fontWeight: 600 }}>{v.violation_description}</span></td>
                    <td style={{ ...tdStyle, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.endpoint}</td>
                    <td style={tdStyle}>{v.request_count}/{v.request_count + (stats.active_windows > 0 ? 0 : v.request_count)}</td>
                    <td style={tdStyle}>{Math.round(v.block_seconds / 60)}m</td>
                    <td style={tdStyle}><code style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{v.mac_address || ', '}</code></td>
                    <td style={tdStyle}><button onClick={(e) => { e.stopPropagation(); setSelectedViolation(selectedViolation === v.id ? null : v.id); }} style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', fontSize: 11 }}>Expand</button></td>
                  </tr>
                  {selectedViolation === v.id && (
                    <tr>
                      <td colSpan={9} style={{ padding: 16, background: 'var(--bg-tertiary)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 12 }}>
                          <div>
                            <strong style={{ color: 'var(--text-primary)' }}>Fingerprint:</strong>
                            <code style={{ display: 'block', color: 'var(--text-secondary)', marginTop: 2 }}>{v.fingerprint}</code>
                          </div>
                          <div>
                            <strong style={{ color: 'var(--text-primary)' }}>User Agent:</strong>
                            <span style={{ display: 'block', color: 'var(--text-secondary)', marginTop: 2, wordBreak: 'break-all' }}>{v.user_agent?.slice(0, 100)}</span>
                          </div>
                          <div>
                            <strong style={{ color: 'var(--text-primary)' }}>ISP:</strong>
                            <span style={{ display: 'block', color: 'var(--text-secondary)' }}>{v.geo?.isp || 'Unknown'}</span>
                          </div>
                          <div>
                            <strong style={{ color: 'var(--text-primary)' }}>Coordinates:</strong>
                            <span style={{ display: 'block', color: 'var(--text-secondary)' }}>
                              {v.geo?.latitude?.toFixed(2)}, {v.geo?.longitude?.toFixed(2)}
                            </span>
                          </div>
                          <div style={{ gridColumn: '1/-1' }}>
                            <strong style={{ color: 'var(--text-primary)' }}>Sample Requests:</strong>
                            {v.request_samples?.slice(0, 5).map((s, si) => (
                              <div key={si} style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 2 }}>
                                {new Date(s.timestamp).toLocaleTimeString()},  {s.method} {s.endpoint}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KPICard({ icon, label, value, color }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ color, marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

const thStyle = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', color: 'var(--text-primary)', whiteSpace: 'nowrap' };
